import { useCallback, useEffect, useMemo, useState } from 'react';
import LinkableTextArea from './LinkableTextArea.jsx';
import RecordDetailPopup from './RecordDetailPopup.jsx';
import {
  getActors,
  getCauses,
  getEmployees,
  getImpacts,
  getLargeCategories,
  getMiddleCategories,
  getSmallCategories,
  getOems,
  getOemRegions,
  getPolicies,
  getProbabilities,
  getRegions,
  getStatuses,
  deleteRisk,
  updateRisk,
} from '../api/dashboardApi.js';
import {
  buildSmallCategoryOptionsByMiddleId,
  buildSmallCategoryLookupByMiddleId,
} from '../utils/converters.js';
import { canEdit } from '../../../auth/permissions.js';
import '../styles/input-controls.css';

/**
 * 進捗入力ページ (DashboardRiskManagementPage) と同じカラム定義・順序。
 */
const BASE_COLUMNS = [
  { key: 'deadlineCheck', label: '期限チェック', type: 'text', readOnly: true },
  { key: 'noId', label: 'ID', type: 'text', readOnly: true },
  { key: 'title', label: 'タイトル', type: 'text', required: true },
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', required: true },
  { key: 'actor', label: '領域', type: 'select', required: true },
  { key: 'largeCategory', label: '大項目', type: 'select' },
  { key: 'middleCategory', label: '中項目', type: 'select' },
  { key: 'smallItem', label: '小項目', type: 'select' },
  { key: 'projectRisk', label: 'PJ全体リスク', type: 'checkbox' },
  { key: 'owner', label: '担当者', type: 'tags', required: true },
  { key: 'status', label: 'ステータス', type: 'select', required: true },
  { key: 'riskOrigin', label: 'リスク起因', type: 'select', required: true },
  { key: 'riskLevel', label: 'リスクレベル', type: 'text', readOnly: true },
  { key: 'probability', label: '発生確率', type: 'select', required: true },
  { key: 'impact', label: '影響度', type: 'select', required: true },
  { key: 'policyDecisionDeadline', label: '対応方針決定期限', type: 'date' },
  { key: 'policyDecisionDate', label: '対応方針決定日', type: 'date' },
  { key: 'responsePolicy', label: 'リスク対応方針', type: 'select' },
  { key: 'policyDetail', label: '対応方針詳細', type: 'text' },
  { key: 'dueDate', label: '対応期限', type: 'date', required: true },
  { key: 'responseStartDate', label: '対応開始日', type: 'date' },
  { key: 'actorCompletedAt', label: '担当領域完了日', type: 'date' },
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'emergenceIssueDate', label: '顕在化による課題移行日', type: 'date' },
  { key: 'pmoConfirmCompletedAt', label: 'PMO確認完了日', type: 'date' },
];

/* ---------- ヘルパー ---------- */

function buildLookupMap(items, key, idKey = 'id') {
  const map = new Map();
  items.forEach((item) => {
    if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      map.set(item[key], item[idKey]);
    }
  });
  return map;
}

function buildMiddleCategoryOptionsByLarge(items) {
  const map = new Map();
  items.forEach((item) => {
    const largeName = item.large_category?.name;
    const middleName = item.middle_category;
    if (!largeName || !middleName) return;
    if (!map.has(largeName)) map.set(largeName, new Set());
    map.get(largeName).add(middleName);
  });
  return Object.fromEntries(
    Array.from(map.entries()).map(([large, middles]) => [large, ['', ...Array.from(middles)]]),
  );
}

// 大項目名→ID
function buildLargeCategoryLookup(items) {
  const map = new Map();
  items.forEach((item) => {
    if (item.large_category && item.id) map.set(item.large_category, item.id);
  });
  return map;
}

// (大項目::中項目)→ID。中項目名は大項目内で一意なので大項目込みのキーで解決する。
function buildMiddleCategoryLookup(items) {
  const map = new Map();
  items.forEach((item) => {
    const largeName = item.large_category?.name;
    const middleName = item.middle_category;
    if (largeName && middleName && item.id) {
      map.set(`${largeName}::${middleName}`, item.id);
    }
  });
  return map;
}

function resolveEmployeeIds(displayValue, employeeByName) {
  /* istanbul ignore if -- 担当者は必須項目でマスタも保存時点で必ずロード済み */
  if (!displayValue || !employeeByName) return [];
  return displayValue
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => employeeByName.get(name))
    .filter(Boolean);
}

export function buildSelectOptionsFromRows(rows, keys) {
  const optionsByKey = Object.fromEntries(keys.map((key) => [key, new Set()]));
  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'owner') {
          String(value).split(',').map((v) => v.trim()).filter(Boolean)
            .forEach((v) => optionsByKey[key].add(v));
          return;
        }
        optionsByKey[key].add(value);
      }
    });
  });
  return Object.fromEntries(
    Object.entries(optionsByKey).map(([key, values]) => [key, ['', ...Array.from(values)]]),
  );
}

function ensureValueInOptions(opts, value) {
  if (!value || opts.includes(value)) return opts;
  return [...opts, value];
}

// master ロード後に1カラム分の選択肢を解決する。
// row(=initialRow)を優先し、カスケード項目は編集中の値(editingSnapshot)も参照する。
// select 以外・特定キー以外のカラムはそのまま返す。
export function resolveRiskColumnOptions(col, master, row, editingSnapshot) {
  const { options } = master;
  let colOptions;
  if (col.key === 'oemRegion') colOptions = options.oemRegion;
  else if (col.key === 'status') colOptions = options.status;
  else if (col.key === 'actor') colOptions = options.actor;
  else if (col.key === 'owner') colOptions = options.owner;
  else if (col.key === 'riskOrigin') colOptions = options.riskOrigin;
  else if (col.key === 'impact') colOptions = options.impact;
  else if (col.key === 'probability') colOptions = options.probability;
  else if (col.key === 'responsePolicy') colOptions = options.responsePolicy;
  else if (col.key === 'largeCategory') colOptions = options.largeCategory;
  else if (col.key === 'middleCategory') {
    // 編集中に選び直した大項目を優先（初期値ではなく現在の選択に連動させる）
    const largeVal = editingSnapshot?.largeCategory || row?.largeCategory;
    colOptions = master.middleCategoryOptionsByLarge?.[largeVal] || [''];
  } else if (col.key === 'smallItem') {
    // 同名中項目の衝突を避けるため、大項目込みで中項目IDを解決してから小項目候補を引く。
    // こちらも編集中の選択（大・中）を優先する。
    const largeVal = editingSnapshot?.largeCategory || row?.largeCategory;
    const middleVal = editingSnapshot?.middleCategory || row?.middleCategory;
    const middleId = master.refs?.middleByLargePair?.get(`${largeVal || ''}::${middleVal || ''}`);
    colOptions = (middleId != null && master.smallCategoryOptionsByMiddleId?.[middleId]) || [''];
  } else if (col.type === 'select') {
    colOptions = options.selectByKey[col.key] || [''];
  } else {
    return col;
  }

  // 現在値の担保は編集中の値を優先（大項目変更でリセットされた中/小の残留を防ぐ）
  const currentValue = editingSnapshot && Object.prototype.hasOwnProperty.call(editingSnapshot, col.key)
    ? editingSnapshot[col.key]
    : row?.[col.key];
  colOptions = ensureValueInOptions(colOptions, currentValue);
  return { ...col, options: colOptions };
}

function toPatchPayload(row, refs) {
  const payload = {};

  /* istanbul ignore else -- タイトルは必須項目なので保存時点で必ず入っている */
  if (row.title) payload.title = row.title;

  if (row.oemRegion && refs.oemRegionLookup?.has(row.oemRegion)) {
    const resolved = refs.oemRegionLookup.get(row.oemRegion);
    payload.oem_id = resolved.oemId;
    payload.region_id = resolved.regionId;
  }

  const statusId = refs.statusByName?.get(row.status);
  if (statusId) payload.status_id = statusId;

  const actorId = refs.actorByName?.get(row.actor);
  if (actorId) payload.actor_id = actorId;

  const causeId = refs.causeByName?.get(row.riskOrigin);
  if (causeId) payload.cause_id = causeId;

  const impactId = refs.impactByName?.get(row.impact);
  if (impactId) payload.impact_id = impactId;

  const probabilityId = refs.probabilityByName?.get(row.probability);
  if (probabilityId) payload.probability_id = probabilityId;

  const policyId = refs.policyByName?.get(row.responsePolicy);
  if (policyId) payload.policy_id = policyId;

  // 大中小項目はリスク自身のカテゴリとして送る（進捗リンクは進捗管理詳細でのみ管理）。
  // 大項目のみ・大中のみの登録も許可するため、解決できない階層は null にする。
  // 小項目は同名中項目の衝突を避けるため、大項目込みで解決した中項目IDでスコープする。
  /* istanbul ignore next -- 大中小は選択式でマスタ由来のため、保存時点では必ず解決できる */
  const middleCategoryId = row.middleCategory
    ? (refs.middleByLargePair?.get(`${row.largeCategory || ''}::${row.middleCategory}`) || null)
    : null;
  
  payload.large_category_id = refs.largeByName?.get(row.largeCategory) || null;
  payload.middle_category_id = middleCategoryId;
  /* istanbul ignore next -- 同上 */
  payload.small_category_id = (row.smallItem && middleCategoryId != null)
    ? (refs.smallByMiddleIdPair?.get(`${middleCategoryId}::${row.smallItem}`) || null)
    : null;

  payload.is_all_problem = Boolean(row.projectRisk);
  /* istanbul ignore next -- 対応期限は必須項目なので空にならない */
  payload.deadline = row.dueDate || null;

  const employeeIds = resolveEmployeeIds(row.owner, refs.employeeByName);
  if (employeeIds.length > 0) {
    payload.employee_ids = employeeIds;
  }

  payload.actor_completed_at = row.actorCompletedAt || null;
  payload.pmo_confirm_completed_at = row.pmoConfirmCompletedAt || null;
  /* istanbul ignore next -- 対応方針決定期限は必須項目なので空にならない */
  payload.policy_decision_deadline = row.policyDecisionDeadline || null;
  payload.policy_decision_date = row.policyDecisionDate || null;
  payload.policy_detail = row.policyDetail || '';
  payload.response_start_date = row.responseStartDate || null;
  payload.emergence_issue_date = row.emergenceIssueDate || null;

  // 楽観ロック用タイムスタンプ（ISO）を送信
  if (row.updated_at_iso) {
    payload.updated_at_iso = row.updated_at_iso;
  }

  return payload;
}

/* ---------- コンポーネント ---------- */

export default function RiskRecordEditor({ open, initialRow, onClose, onSaved, onDeleted, readOnly: readOnlyProp }) {
  const readOnly = readOnlyProp !== undefined ? readOnlyProp : !canEdit('ダッシュボード');
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [master, setMaster] = useState(null);
  const [masterLoading, setMasterLoading] = useState(false);

  useEffect(() => {
    if (!open || master) return;
    let active = true;
    setMasterLoading(true);

    Promise.all([
      getStatuses(),
      getActors(),
      getCauses(),
      getImpacts(),
      getProbabilities(),
      getPolicies(),
      getOems(),
      getRegions(),
      getOemRegions(),
      getLargeCategories(),
      getMiddleCategories(),
      getSmallCategories(),
      getEmployees(),
    ])
      .then(([
        statuses, actors, causes, impacts,
        probabilities, policies,
        _oems, _regions, oemRegions,
        largeCategories, middleCategories,
        smallCategories, employees,
      ]) => {
        if (!active) return;

        const statusByName = buildLookupMap(statuses, 'status');
        const actorByName = buildLookupMap(actors, 'actor');
        const causeByName = buildLookupMap(causes, 'cause');
        const impactByName = buildLookupMap(impacts, 'impact');
        const probabilityByName = buildLookupMap(probabilities, 'probability');
        const policyByName = buildLookupMap(policies, 'policy');
        const employeeByName = buildLookupMap(employees, 'user_name', 'employee_id');

        const oemRegionOptions = [''];
        const oemRegionLookup = new Map();
        oemRegions.forEach((item) => {
          const oemName = item.oem?.name || '';
          const regionName = item.region?.name || '';
          if (oemName && regionName) {
            const label = `${oemName}×${regionName}`;
            if (!oemRegionLookup.has(label)) {
              oemRegionOptions.push(label);
              oemRegionLookup.set(label, {
                oemId: item.oem.id,
                regionId: item.region.id,
              });
            }
          }
        });

        const statusOptions = ['', ...statuses.map((i) => i.status).filter(Boolean)];
        const actorOptions = ['', ...actors.map((i) => i.actor).filter(Boolean)];
        const causeOptions = ['', ...causes.map((i) => i.cause).filter(Boolean)];
        const impactOptions = ['', ...impacts.map((i) => i.impact).filter(Boolean)];
        const probabilityOptions = ['', ...probabilities.map((i) => i.probability).filter(Boolean)];
        const policyOptions = ['', ...policies.map((i) => i.policy).filter(Boolean)];
        const employeeOptions = ['', ...employees.map((i) => i.user_name).filter(Boolean)];
        const largeCategoryOpts = [
          '',
          ...Array.from(new Set(largeCategories.map((i) => i.large_category).filter(Boolean))),
        ];
        const middleCategoryOptionsByLargeMap = buildMiddleCategoryOptionsByLarge(middleCategories);
        const smallCategoryOptionsByMiddleIdMap = buildSmallCategoryOptionsByMiddleId(smallCategories);

        // カテゴリ名→ID のルックアップ（リスク自身のカテゴリFKを解決するため）
        const largeByName = buildLargeCategoryLookup(largeCategories);
        const middleByLargePair = buildMiddleCategoryLookup(middleCategories);
        const smallByMiddleIdPair = buildSmallCategoryLookupByMiddleId(smallCategories);

        setMaster({
          refs: {
            statusByName,
            actorByName,
            causeByName,
            impactByName,
            probabilityByName,
            policyByName,
            employeeByName,
            oemRegionLookup,
            largeByName,
            middleByLargePair,
            smallByMiddleIdPair,
          },
          options: {
            oemRegion: oemRegionOptions,
            status: statusOptions,
            actor: actorOptions,
            owner: employeeOptions,
            riskOrigin: causeOptions,
            impact: impactOptions,
            probability: probabilityOptions,
            responsePolicy: policyOptions,
            largeCategory: largeCategoryOpts,
            selectByKey: {},
          },
          middleCategoryOptionsByLarge: middleCategoryOptionsByLargeMap,
          smallCategoryOptionsByMiddleId: smallCategoryOptionsByMiddleIdMap,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (active) setMasterLoading(false);
      });

    return () => { active = false; };
  }, [open, master]);

  useEffect(() => {
    if (initialRow) {
      setEditingRow({
        ...initialRow,
        projectRisk: Boolean(initialRow.projectRisk),
      });
    } else {
      setEditingRow(null);
    }
  }, [initialRow]);

  const columns = useMemo(() => {
    const row = initialRow;
    if (!master) {
      return BASE_COLUMNS.map((col) => {
        if (col.type !== 'select') return col;
        const value = row?.[col.key];
        const opts = value ? ['', value] : [''];
        return { ...col, options: opts };
      });
    }
    const editingSnapshot = {
      largeCategory: editingRow?.largeCategory,
      middleCategory: editingRow?.middleCategory,
      smallItem: editingRow?.smallItem,
      oemRegion: editingRow?.oemRegion,
    };
    return BASE_COLUMNS.map((col) => resolveRiskColumnOptions(col, master, row, editingSnapshot));
  }, [master, initialRow, editingRow?.largeCategory, editingRow?.middleCategory, editingRow?.smallItem, editingRow?.oemRegion]);

  const updateField = useCallback((key, value) => {
    setEditingRow((prev) => {
      /* istanbul ignore if -- 編集中のみ呼ばれるため prev は常に存在する */
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
      if (key === 'largeCategory') {
        const middleOpts = master?.middleCategoryOptionsByLarge?.[value] || [''];
        if (!middleOpts.includes(next.middleCategory || '')) {
          next.middleCategory = '';
          next.smallItem = '';
        } else {
          // 中項目名は残せても、大項目が変わると中項目IDが変わり小項目候補も変わる
          const middleId = master?.refs?.middleByLargePair?.get(`${value || ''}::${next.middleCategory || ''}`);
          const smallOpts = (middleId != null && master?.smallCategoryOptionsByMiddleId?.[middleId]) || [''];
          if (!smallOpts.includes(next.smallItem || '')) {
            next.smallItem = '';
          }
        }
      }
      if (key === 'middleCategory') {
        const middleId = master?.refs?.middleByLargePair?.get(`${next.largeCategory || ''}::${value || ''}`);
        const smallOpts = (middleId != null && master?.smallCategoryOptionsByMiddleId?.[middleId]) || [''];
        if (!smallOpts.includes(next.smallItem || '')) {
          next.smallItem = '';
        }
      }
      return next;
    });
  }, [master]);

  const renderField = useCallback((row, column) => {
    if (column.type === 'tags') {
      
      const opts = column.options || [''];
      const raw = row[column.key] || '';
      const filled = raw.split(',').map((s) => s.trim()).filter(Boolean);
      const hasNewSlot = raw.trimEnd().endsWith(',');
      const items = hasNewSlot ? [...filled, ''] : filled;

      const addItem = () => {
        const current = row[column.key] || '';
        const trimmed = current.replace(/,\s*$/, '');
        updateField(column.key, trimmed ? trimmed + ',' : ',');
      };

      if (items.length === 0) {
        return (
          <button
            type="button"
            onClick={addItem}
            style={{
              border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
              padding: '4px 12px', fontSize: '13px', color: '#6b7280', cursor: 'pointer',
            }}
          >
            ＋ 追加
          </button>
        );
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {items.map((item, i) => {
            const selectOpts = opts.includes(item) ? opts : [...opts, item];
            return (
              <div key={`${column.key}-${i}`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <select
                  className="db-select-control"
                  value={item}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[i] = e.target.value;
                    updateField(column.key, newItems.filter(Boolean).join(', '));
                  }}
                  style={{ flex: 1 }}
                >
                  {selectOpts.map((opt) => (
                    <option key={opt || 'empty'} value={opt}>
                      {opt || '選択してください'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newItems = items.filter((_, idx) => idx !== i);
                    updateField(column.key, newItems.join(', '));
                  }}
                  style={{
                    border: 'none', background: 'transparent', color: '#ef4444',
                    fontSize: '16px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
                  }}
                  title="削除"
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addItem}
            style={{
              border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
              padding: '3px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            ＋ 追加
          </button>
        </div>
      );
    }

    if (column.type === 'select') {
      /* istanbul ignore next -- この select 列は列定義側で options を必ず設定している */
      const opts = column.options || [''];

      return (
        <select
          className="db-select-control"
          onChange={(e) => {
            updateField(column.key, e.target.value);
          }}
          value={row[column.key] || ''}
        >
          {opts.map((option) => (
            <option key={option || 'empty'} value={option}>
              {option || '選択してください'}
            </option>
          ))}
        </select>
      );
    }

    if (column.type === 'checkbox') {
      return (
        <input
          checked={Boolean(row[column.key])}
          onChange={(e) => updateField(column.key, e.target.checked)}
          type="checkbox"
        />
      );
    }

    if (column.type === 'date') {
      return (
        <input
          className="db-input-control"
          max="9999-12-31"
          onChange={(e) => updateField(column.key, e.target.value)}
          type="date"
          value={row[column.key] || ''}
        />
      );
    }

    if (['policyDetail'].includes(column.key)) {
      return (
        <LinkableTextArea
          onChange={(next) => updateField(column.key, next)}
          value={row[column.key]}
        />
      );
    }

    return (
      <input
        className="db-input-control"
        onChange={(e) => updateField(column.key, e.target.value)}
        type="text"
        value={row[column.key] || ''}
      />
    );
  }, [master, updateField]);

  const formatValue = useCallback((column, value) => {
    if (column.type === 'checkbox') return value ? 'はい' : 'いいえ';
    return value || '—';
  }, []);

  const handleDelete = useCallback(async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row?.id) return;
    setDeleting(true);
    setSaveError('');
    try {
      await deleteRisk(row.id, row.updated_at_iso ?? null);
      setEditingRow(null);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      console.error('[RiskRecordEditor] Delete error:', err);
      setSaveError(err.message || '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  }, [onDeleted, onClose]);

  const handleSave = useCallback(async () => {
    if (!editingRow?.id) {
      setSaveError('レコードIDが見つかりません。ポップアップを閉じて再度開いてください。');
      return;
    }

    const missingFields = columns
      .filter((c) => c.required && !c.readOnly)
      .filter((c) => {
        const val = editingRow[c.key];
        /* istanbul ignore if -- 必須列はすべて文字列項目のため配列にならない */
        if (Array.isArray(val)) return val.length === 0;
        /* istanbul ignore if -- 必須列にチェックボックスは無い */
        if (typeof val === 'boolean') return false;
        return !val && val !== 0;
      })
      .map((c) => c.label);
    if (missingFields.length > 0) {
      setSaveError(`以下の必須項目が未入力です：\n${missingFields.join('、')}`);
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      /* istanbul ignore next -- マスタ未ロード中は保存ボタンが無効なのでここへ来ない */
      const refs = master?.refs || {};
      const payload = toPatchPayload(editingRow, refs);
      await updateRisk(editingRow.id, payload);
      setEditingRow(null);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[RiskRecordEditor] Save error:', err);
      if (err.status === 409) {
        setSaveError('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      } else {
        setSaveError(err.message || '保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  }, [editingRow, master, columns, onSaved, onClose]);

  const handleClose = useCallback(() => {
    setSaveError('');
    onClose?.();
  }, [onClose]);

  return (
    <>
      <RecordDetailPopup
        columns={columns}
        deleting={deleting}
        formatValue={formatValue}
        onClose={handleClose}
        onDelete={readOnly ? undefined : handleDelete}
        onSave={readOnly ? undefined : handleSave}
        open={open && Boolean(editingRow)}
        readOnly={readOnly}
        renderField={renderField}
        row={editingRow}
        saving={saving}
        savingDisabled={masterLoading || !master}
        title="リスク管理レコード詳細"
      />
      {saveError && (
        <div className="db-error-modal-overlay" onClick={() => setSaveError('')} role="presentation">
          <div className="db-error-modal" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="db-error-modal-icon">!</div>
            <p className="db-error-modal-message">{saveError}</p>
            <button className="db-error-modal-button" onClick={() => setSaveError('')} type="button">
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
